import { useGetMeeting } from '@repo/api/meeting/useGetMeeting';
import { cn } from '@repo/lib/cn';
import { format } from 'date-fns';
import { BuildingIcon, CalendarRangeIcon, ClockAlertIcon, MapPinnedIcon } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import EventActionButtonGroup from '@/components/features/calendar/EventActionButtonGroup';
import CancelMeeting from '@/components/features/calendar/form/CancelMeeting';
import DeleteMeeting from '@/components/features/calendar/form/DeleteMeeting';
import EditMeeting from '@/components/features/calendar/form/EditMeeting';
import LocationCard from '@/components/features/location/LocationCard';
import ErrorState from '@/components/features/meeting/ErrorState';
import MeetingAttendance from '@/components/features/meeting/MeetingAttendance';
import MeetingMembers from '@/components/features/meeting/MeetingMembers';
import MeetingPageSkeleton from '@/components/features/meeting/MeetingPageSkeleton';
import MeetingRemoteLink from '@/components/features/meeting/MeetingRemoteLink';
import { Badge } from '@/components/ui/badge';
import { useCredentials } from '@/store/auth-store';

export default function MeetingPage() {
  const { id } = useParams();
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [openCancelDialog, setOpenCancelDialog] = useState<boolean>(false);
  const { data, isPending, isError, errorMessage } = useGetMeeting(Number(id));
  const { lvl } = useCredentials();
  const navigate = useNavigate();

  // Error state
  if (isError) {
    return <ErrorState errorMessage={errorMessage} />;
  }

  // Loading state
  if (isPending) {
    return <MeetingPageSkeleton />;
  }

  return (
    <div className={'base-direction-row'}>
      {/* edit meeting dialog */}
      {lvl > 0 && (
        <>
          <DeleteMeeting open={openDeleteDialog} onOpenChange={setOpenDeleteDialog} event={data} callback={() => navigate('/calendar')} />
          <EditMeeting id={Number(id)} open={openEditDialog} onOpenChange={setOpenEditDialog} />
          <CancelMeeting open={openCancelDialog} onOpenChange={setOpenCancelDialog} event={data} />
        </>
      )}
      {/* main data container */}
      <div className={'bg-background border-border-primary h-full w-full divide-y rounded-2xl border'}>
        {/* page header */}
        <header className={'flex flex-wrap items-center gap-2 p-4'}>
          <div className={cn('h-10 w-1 max-w-1 min-w-1 grow rounded-xs', data.type === 'remote' ? 'bg-brand' : 'bg-accent', data.isCancelled && 'bg-destructive')} />
          <h2 className={'truncate text-xl font-semibold'}>{data.name}</h2>
          {/* edit button */}
          {lvl > 0 && (
            <EventActionButtonGroup
              onEdit={() => setOpenEditDialog(true)}
              onDelete={() => setOpenDeleteDialog(true)}
              onCancel={() => setOpenCancelDialog(true)}
              isCancelled={data.isCancelled}
              className={'ml-auto'}
            />
          )}
        </header>
        {/* meeting data */}
        <div className="grid w-full grid-cols-[auto_1fr] gap-x-2 gap-y-3 p-4">
          {/* Type row */}
          <div className="flex items-center gap-1">
            <MapPinnedIcon className="text-muted-foreground size-4" />
            <p className="text-muted-foreground text-sm">Typ:</p>
          </div>
          <div className="text-accent-foreground text-sm font-medium capitalize">{data.type === 'remote' ? 'Zdalne' : 'Stacjonarne'}</div>

          {data.type === 'stationary' && data.location && (
            <>
              <div className="flex items-center gap-1">
                <BuildingIcon className="text-muted-foreground size-4" />
                <p className="text-muted-foreground text-sm">Lokacja:</p>
              </div>
              <p className="text-accent-foreground text-sm font-medium capitalize">{data.location.name}</p>
            </>
          )}
          <div className="flex items-center gap-1">
            <CalendarRangeIcon className="text-muted-foreground size-4" />
            <p className="text-muted-foreground text-sm">Data:</p>
          </div>
          <div className="text-accent-foreground text-sm font-medium">{`${format(data.startDate, 'do MMM yyyy')} | ${format(data.startDate, 'HH:mm')}-${format(data.endDate, 'HH:mm')}`}</div>
          <div className="flex items-center gap-1">
            <ClockAlertIcon className="text-muted-foreground size-4" />
            <p className="text-muted-foreground text-sm">Status:</p>
          </div>
          <Badge size={'sm'} variant={data.isCancelled ? 'destructive' : 'success'}>
            {data.isCancelled ? 'Odwołano' : 'Aktywne'}
          </Badge>
        </div>
        {/* meeting description */}
        <div className="space-y-3 p-4">
          <h2 className={'text-md text-foreground font-medium'}>Opis</h2>
          <div className={'w-full'}>{data.desc ? <p className={'text-accent-foreground text-sm'}>{data.desc}</p> : <p className="text-muted-foreground text-sm">brak opisu</p>}</div>
        </div>
        {/* meeting location or meeting link, display only if there is data */}
        {((data.type == 'stationary' && data.location?.id) || (data.type == 'remote' && data.meetingURL)) && (
          <>
            <div className="space-y-3 p-4">
              <h2 className={'text-md text-foreground font-medium'}>{data.type == 'stationary' ? 'Lokacja' : 'Link do spotkania'}</h2>
              <div>{data.type == 'stationary' ? data.location && <LocationCard location={data.location} className="h-82 md:h-96" /> : <MeetingRemoteLink meeting={data} />}</div>
            </div>
          </>
        )}
      </div>
      {/* meeting attendance or members */}
      <div className="border-border-primary bg-background w-full rounded-2xl border md:w-xl">
        {lvl <= 0 ? <MeetingMembers groups={data.groups} users={data.users} /> : <MeetingAttendance meetingId={data.id} attendance={data.attendances} />}
      </div>
    </div>
  );
}
